const DRAFT_KEY_PREFIX = 'counselpro:support:draft:'
const DRAFT_LIFETIME_MS = 14 * 24 * 60 * 60 * 1000

interface StoredDraft {
  body: string
  savedAt: number
}

const draftStorageKey = (ticketPublicId: string) =>
  `${DRAFT_KEY_PREFIX}${ticketPublicId}`

const parseStoredDraft = (raw: string | null): StoredDraft | null => {
  if (!raw) return null

  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null

    const { body, savedAt } = parsed as Partial<StoredDraft>
    if (typeof body !== 'string' || typeof savedAt !== 'number') return null

    return { body, savedAt }
  } catch {
    return null
  }
}

export const readDraft = (ticketPublicId: string): string => {
  try {
    const raw = localStorage.getItem(draftStorageKey(ticketPublicId))
    return parseStoredDraft(raw)?.body ?? ''
  } catch {
    return ''
  }
}

export const writeDraft = (ticketPublicId: string, body: string) => {
  const storageKey = draftStorageKey(ticketPublicId)
  try {
    if (!body) {
      localStorage.removeItem(storageKey)
      return
    }
    const stored: StoredDraft = { body, savedAt: Date.now() }
    localStorage.setItem(storageKey, JSON.stringify(stored))
  } catch {
    // A full or blocked storage must never stop the user from typing.
  }
}

// The composer empties the moment a reply is handed over, but storage keeps the
// text until the service confirms it. Anything the user has written since then
// is a different draft and stays.
export const forgetDraftIfUnchanged = (
  ticketPublicId: string,
  sentBody: string,
) => {
  if (readDraft(ticketPublicId) === sentBody) writeDraft(ticketPublicId, '')
}

// A send that failed keeps its text in storage so a reload can still recover
// it, but the same text is already on screen in the red bubble. Putting it back
// in the composer as well would show the reply twice and let Send post a second
// copy, so a stored draft that a live reply is still holding stays out.
export const restorableDraft = (
  ticketPublicId: string,
  pendingBodies: readonly string[],
) => {
  const stored = readDraft(ticketPublicId)
  return pendingBodies.includes(stored) ? '' : stored
}

const storedDraftKeys = () => {
  const keys: string[] = []
  for (let position = 0; position < localStorage.length; position += 1) {
    const key = localStorage.key(position)
    if (key?.startsWith(DRAFT_KEY_PREFIX)) keys.push(key)
  }
  return keys
}

// An entry that cannot be read can never be restored, so it is only taking up
// room and goes with the expired ones.
const isExpired = (storageKey: string, now: number) => {
  const stored = parseStoredDraft(localStorage.getItem(storageKey))
  return !stored || now - stored.savedAt > DRAFT_LIFETIME_MS
}

export const sweepExpiredDrafts = (now = Date.now()) => {
  try {
    for (const storageKey of storedDraftKeys()) {
      if (isExpired(storageKey, now)) localStorage.removeItem(storageKey)
    }
  } catch {
    // A failed sweep only leaves the old drafts where they were.
  }
}
